/**
 * Handle audio for the frontend to work
 * @creator Stefano
 * @date 26-6-2018
 */
import { Howl } from "howler";
import Tooltip from "./utils/Tooltip";

export default class IssueAudio {
    constructor() {
        this._spread_audio_muted = false;
        this.audio = null;
        this.sources = null;
        this.audioButton = document.getElementById("audio-button");

        this.audioButton &&
            this.audioButton.addEventListener("click", () => {
                this.toggleSpreadAudio();
            });
    }

    /**
     * Stop and clear the current spread audio
     *
     * @method stopSpreadAudio
     */
    stopSpreadAudio() {
        if (this.audio) {
            this.audio.unload();
            this.audio = null;
            this.sources = null;

            //hide button
            this.setButtonClass();
        }
    }

    /**
     * Play the given sources array with the Howl plugin. Howl will know which source to use for each browser.
     * Always play the audio but when the user muted the spread sound mute it before start.
     *
     * @method playSpreadAudio
     * @param array sources
     */
    playSpreadAudio(sources) {
        this.stopSpreadAudio();
        this.audio = this._getNewSpreadAudioInstance(sources);

        if (this._spread_audio_muted === true) {
            this.audio.mute(true);
        } else {
            this.audio.mute(false);
        }
        this.currentSoundId = this.audio.play();
        this.audio.fade(0, 1, 1000);
        this.setButtonClass();

        if (this.audioButton) {
            this.audio.once("play", () => {
                this.audioButton.classList.remove("attention-seeker");
            });
            this.audio.once("load", () => {
                let playing = this.audio.playing(this.currentSoundId);
                if (!playing) {
                    //audio not playing as requested
                    try {
                        const tip = Tooltip.createTooltip(
                            this.audioButton,
                            { text: window.interface_settings.settings?.text?.audio_tooltip },
                            { interactive: false },
                        ).show();
                        setTimeout(() => tip.hide(), 3000);

                        this.audioButton.classList.add("attention-seeker");
                    } catch (error) {
                        console.error("MAGLR ERROR: [audio.load]", error);
                    }
                    this._spread_audio_muted = true;
                    this.audio.volume(1);
                }
                this.setButtonClass();
            });
        }
    }

    _getNewSpreadAudioInstance(sources) {
        this.sources = sources;
        return new Howl({
            src: sources.map((file) => window.data_domain + file),
            html5: true,
            buffer: true,
            loop: true,
            volume: 0,
            mobileAutoEnable: false,
            mute: this._spread_audio_muted,
        });
    }

    /**
     * Mute or unmute the spread audio sound if there is any.
     *
     * @method muteSpreadAudio
     * @param {bool} bool
     */
    muteSpreadAudio(bool) {
        if (this.audio) {
            this.audio.mute(bool);

            if (!bool) {
                !this.audio.playing(this.currentSoundId) && this.audio.play(this.currentSoundId);
                this.audio.fade(0, 1, 500);
            }

            this.setButtonClass();
        }
    }

    /**
     * Toggle for the mute boolean in the interface and calls the actual sound mute right after..
     *
     * @method toggleSpreadAudio
     */
    toggleSpreadAudio() {
        this._spread_audio_muted = !this._spread_audio_muted;
        this.muteSpreadAudio(this._spread_audio_muted);
    }

    setButtonClass() {
        if (!this.audioButton) {
            return;
        }

        if (!this.audio) {
            this.audioButton.classList.remove("maglr-icon-audio-on");
            this.audioButton.classList.remove("maglr-icon-audio-off");
        } else {
            //switch muted state
            if (this._spread_audio_muted) {
                this.audioButton.classList.add("maglr-icon-audio-off");
                this.audioButton.classList.remove("maglr-icon-audio-on");
            } else {
                this.audioButton.classList.add("maglr-icon-audio-on");
                this.audioButton.classList.remove("maglr-icon-audio-off");
            }
        }
    }

    checkSameSource(sources) {
        return this.sources && this.sources[0] === sources[0];
    }
}
